import BaseApi from "apps/visits/lib/BaseApi";
import { createRoute } from "apps/visits/packages/shared/models/Route";
import { HTTPMethods } from "apps/visits/packages/shared/static/http-methods";
import type { Order } from "../models";
import type {
  AvailableSlotsResponse,
  CreateOrderDTO,
  CreateSessionDTO,
  CreateSessionResponse,
  LoginDTO,
  PatientAuthenticationResponse,
  ResendVerificationCodeDTO,
  ResendVerificationCodeResponse,
  SelectTimeSlotDTO,
  SimplifyRegisterDTO,
  Slug,
  SlugParam,
  SlugWithUuidParam,
  StandardRegisterDTO,
  VerifyPhoneNumberDTO,
  VerifyPhoneNumberResponse,
} from "./group-actions-ordering.types";

export const routes = {
  getAvailableSlots: createRoute<SlugParam>("/api/v2/group_actions_signup/group_actions/:slug/available_slots"),
  verifyPhoneNumber: createRoute<SlugParam>("/api/v2/group_actions_signup/group_actions/:slug/phone_verifications"),
  createSession: createRoute<SlugParam>("/api/v2/group_actions_signup/group_actions/:slug/verification_sessions"),
  login: createRoute<SlugParam>("/api/v2/group_actions_signup/group_actions/:slug/authentications"),
  resendVerificationCode: createRoute<SlugParam>("/api/v2/group_actions_signup/group_actions/:slug/token_refreshes"),
  register: createRoute<SlugParam>("/api/v2/group_actions_signup/group_actions/:slug/registrations"),
  selectTimeSlot: createRoute<SlugWithUuidParam>("/api/v2/group_actions_signup/group_actions/:slug/orders/:uuid"),
  createOrder: createRoute<SlugParam>("/api/v2/group_actions_signup/group_actions/:slug/orders"),
  getOrder: createRoute<SlugWithUuidParam>("/api/v2/group_actions_signup/group_actions/:slug/orders/:uuid"),
  confirmOrder: createRoute<SlugWithUuidParam>("/api/v2/group_actions_signup/group_actions/:slug/orders/:uuid/confirm"),
};

export class GroupActionsOrderingRepository extends BaseApi {
  async getAvailableSlots(slug: Slug) {
    return this.request<AvailableSlotsResponse>({
      url: routes.getAvailableSlots.apply({ slug }),
      method: HTTPMethods.GET,
    });
  }

  async verifyPhoneNumber({ ...data }: VerifyPhoneNumberDTO, slug: Slug) {
    return this.request<VerifyPhoneNumberResponse>({
      url: routes.verifyPhoneNumber.apply({ slug }),
      method: HTTPMethods.POST,
      data,
    });
  }

  async register({ ...data }: StandardRegisterDTO | SimplifyRegisterDTO, slug: Slug) {
    return this.request<PatientAuthenticationResponse>({
      url: routes.register.apply({ slug }),
      method: HTTPMethods.POST,
      data,
    });
  }

  async selectTimeSlot({ ...data }: SelectTimeSlotDTO, uuid: Order["uuid"], slug: Slug) {
    return this.request<void>({
      url: routes.selectTimeSlot.apply({ slug, uuid }),
      method: HTTPMethods.PATCH,
      data,
    });
  }

  async createSession({ ...data }: CreateSessionDTO, slug: Slug) {
    return this.request<CreateSessionResponse>({
      url: routes.createSession.apply({ slug }),
      method: HTTPMethods.POST,
      data,
    });
  }

  async login({ ...data }: LoginDTO, slug: Slug) {
    return this.request<PatientAuthenticationResponse>({
      url: routes.login.apply({ slug }),
      method: HTTPMethods.POST,
      data,
    });
  }

  async resendVerificationCode({ ...data }: ResendVerificationCodeDTO, slug: Slug) {
    return this.request<ResendVerificationCodeResponse>({
      url: routes.resendVerificationCode.apply({ slug }),
      method: HTTPMethods.POST,
      data,
    });
  }

  async createOrder({ ...data }: CreateOrderDTO, slug: Slug) {
    return this.request<{ order: Order }>({
      url: routes.createOrder.apply({ slug }),
      method: HTTPMethods.POST,
      data,
    });
  }

  async getOrder(uuid: Order["uuid"], slug: Slug) {
    return this.request<{ order: Order }>({
      url: routes.getOrder.apply({ uuid, slug }),
      method: HTTPMethods.GET,
    });
  }

  async confirmOrder(uuid: Order["uuid"], slug: Slug) {
    return this.request<{ order: Order }>({
      url: routes.confirmOrder.apply({ uuid, slug }),
      method: HTTPMethods.POST,
    });
  }
}
